import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '../../../../store';
import { SessionListResponse } from '../../../../interfaces/sessions';
import { CustomDefinition, CustomMetricSource, CustomMetricValueType } from '../../../../interfaces/db';
import { getCustomCode, OutputType, CustomCode } from '../../../../components/CustomScores/cfg';

export interface FormattedCustomScore {
  sessionId: string;
  label: string;
  displayValue: string;
  rawValue: any;
  isPositive?: boolean;
  explanation?: string;
  outputType: OutputType | 'unknown';
  colorCategory: 'positive' | 'negative' | 'neutral' | 'category';
  backgroundColor: string;
  textColor: string;
  borderColor: string;
}

interface UseCustomScoreFormatterProps {
  session: SessionListResponse;
  definition?: CustomDefinition;
  customCode?: CustomCode;
  rawValue: any;
  explanation?: string;
}

/**
 * Generate consistent colors for categorical values based on hash
 */
function getCategoryColors(value: string): { bg: string; text: string; border: string } {
  // Simple hash function to get consistent colors
  let hash = 0;
  for (let i = 0; i < value.length; i++) {
    hash = value.charCodeAt(i) + ((hash << 5) - hash);
  }
  
  // Use hash to pick from predefined color palette
  const colors = [
    { bg: '#fef3c7', text: '#92400e', border: '#fcd34d' }, // amber
    { bg: '#dbeafe', text: '#1e40af', border: '#93c5fd' }, // blue  
    { bg: '#f3e8ff', text: '#7c2d12', border: '#c4b5fd' }, // violet
    { bg: '#ecfdf5', text: '#065f46', border: '#86efac' }, // emerald
    { bg: '#fdf2f8', text: '#be185d', border: '#f9a8d4' }, // pink
    { bg: '#f0f9ff', text: '#0c4a6e', border: '#7dd3fc' }, // sky
    { bg: '#fff7ed', text: '#9a3412', border: '#fed7aa' }, // orange
    { bg: '#f8fafc', text: '#475569', border: '#cbd5e1' }, // slate
  ];
  
  const colorIndex = Math.abs(hash) % colors.length;
  return colors[colorIndex];
}

/**
 * Utility function to format a single custom score value
 * Follows the exact pattern from ScoreMetric.tsx with enhanced coloring
 */
export function formatCustomScore({
  session,
  definition,
  customCode,
  rawValue,
  explanation,
  teamCustomCodes
}: UseCustomScoreFormatterProps & { teamCustomCodes?: CustomCode[] }): FormattedCustomScore | null {
  if (!definition) {
    return null;
  }

  // If value is null, return null (like ScoreMetric.tsx does)
  if (rawValue === null || rawValue === undefined) {
    return null;
  }

  // Handle Date or object values (return null like ScoreMetric.tsx)
  if (rawValue instanceof Date || (typeof rawValue === 'object' && rawValue !== null)) {
    return null;
  }

  let displayValue = '';
  let isPositive: boolean | undefined;
  let outputType: OutputType | 'unknown' = 'unknown';
  let colorCategory: 'positive' | 'negative' | 'neutral' | 'category' = 'neutral';
  let backgroundColor = '#f3f4f6';
  let textColor = '#374151';
  let borderColor = '#d1d5db';

  // Handle custom code metrics (following ScoreMetric.tsx pattern exactly)
  if (definition.value_source === CustomMetricSource.CODE && definition.value_code_str && teamCustomCodes) {
    const code = teamCustomCodes.find(c => c.name === definition.value_code_str);
    
    if (code) {
      outputType = code.output_type;
      
      switch (code.output_type) {
        case OutputType.BOOLEAN:
          isPositive = Boolean(rawValue);
          displayValue = Boolean(rawValue) ? 'Yes' : 'No';
          if (Boolean(rawValue)) {
            colorCategory = 'positive';
            backgroundColor = '#f0fdf4';
            textColor = '#166534';
            borderColor = '#bbf7d0';
          } else {
            colorCategory = 'negative';
            backgroundColor = '#fef2f2';
            textColor = '#dc2626';
            borderColor = '#fecaca';
          }
          break;
        case OutputType.PERCENTAGE:
          const percentage = rawValue !== null ? Math.round(Number(rawValue) * 100) : 0;
          displayValue = `${percentage}%`;
          if (percentage >= 70) {
            isPositive = true;
            colorCategory = 'positive';
            backgroundColor = '#f0fdf4';
            textColor = '#166534';
            borderColor = '#bbf7d0';
          } else if (percentage >= 40) {
            isPositive = undefined;
            colorCategory = 'neutral';
            backgroundColor = '#fffbeb';
            textColor = '#92400e';
            borderColor = '#fcd34d';
          } else {
            isPositive = false;
            colorCategory = 'negative';
            backgroundColor = '#fef2f2';
            textColor = '#dc2626';
            borderColor = '#fecaca';
          }
          break;
        case OutputType.ENUM:
          if (!code.enum_options || rawValue === null) {
            displayValue = '-';
            break;
          }
          displayValue = code.enum_options[Number(rawValue)] || '-';
          // Use category colors for enum values
          colorCategory = 'category';
          const categoryColors = getCategoryColors(displayValue);
          backgroundColor = categoryColors.bg;
          textColor = categoryColors.text;
          borderColor = categoryColors.border;
          break;
        default:
          displayValue = String(rawValue || '-');
      }
    } else {
      displayValue = String(rawValue || '-');
    }
  } else {
    // Handle standard metrics (following ScoreMetric.tsx pattern exactly)
    switch (definition.value_type) {
      case CustomMetricValueType.BOOLEAN:
        isPositive = Boolean(rawValue);
        displayValue = Boolean(rawValue) ? 'Yes' : 'No';
        outputType = OutputType.BOOLEAN;
        if (Boolean(rawValue)) {
          colorCategory = 'positive';
          backgroundColor = '#f0fdf4';
          textColor = '#166534';
          borderColor = '#bbf7d0';
        } else {
          colorCategory = 'negative';
          backgroundColor = '#fef2f2';
          textColor = '#dc2626';
          borderColor = '#fecaca';
        }
        break;
      case CustomMetricValueType.PERCENT:
        const percentage = rawValue !== null ? Math.round(Number(rawValue) * 100) : 0;
        displayValue = `${percentage}%`;
        outputType = OutputType.PERCENTAGE;
        if (percentage >= 70) {
          isPositive = true;
          colorCategory = 'positive';
          backgroundColor = '#f0fdf4';
          textColor = '#166534';
          borderColor = '#bbf7d0';
        } else if (percentage >= 40) {
          isPositive = undefined;
          colorCategory = 'neutral';
          backgroundColor = '#fffbeb';
          textColor = '#92400e';
          borderColor = '#fcd34d';
        } else {
          isPositive = false;
          colorCategory = 'negative';
          backgroundColor = '#fef2f2';
          textColor = '#dc2626';
          borderColor = '#fecaca';
        }
        break;
      case CustomMetricValueType.ENUM:
        if (!definition.value_type_enum_str || rawValue === null) {
          displayValue = '-';
          break;
        }
        const categories = definition.value_type_enum_str.split(',');
        displayValue = categories[Number(rawValue)] || '-';
        outputType = OutputType.ENUM;
        // Use category colors for enum values
        colorCategory = 'category';
        const categoryColors = getCategoryColors(displayValue);
        backgroundColor = categoryColors.bg;
        textColor = categoryColors.text;
        borderColor = categoryColors.border;
        break;
      default:
        displayValue = String(rawValue || '-');
    }
  }

  return {
    sessionId: session.session_id,
    label: definition.label || 'Unknown Metric',
    displayValue,
    rawValue,
    isPositive,
    explanation,
    outputType,
    colorCategory,
    backgroundColor,
    textColor,
    borderColor
  };
}

/**
 * Hook to format custom score values based on their type (boolean, enum, percentage, etc.)
 * Handles Attyx custom codes and regular custom definitions
 */
export function useCustomScoreFormatter(props: UseCustomScoreFormatterProps): FormattedCustomScore | null {
  const teamId = useSelector((state: RootState) => state.team.value?.team_id);
  const teamCustomCodes = useMemo(() => getCustomCode(teamId || ''), [teamId]);

  return useMemo(() => {
    return formatCustomScore({ ...props, teamCustomCodes });
  }, [props, teamCustomCodes]);
}

/**
 * Hook to format multiple custom scores for a session
 */
export function useCustomScoresFormatter(
  session: SessionListResponse,
  customScores: Array<{
    definition?: CustomDefinition;
    customCode?: CustomCode;
    rawValue: any;
    explanation?: string;
  }>
): FormattedCustomScore[] {
  const teamId = useSelector((state: RootState) => state.team.value?.team_id);
  const teamCustomCodes = useMemo(() => getCustomCode(teamId || ''), [teamId]);

  return useMemo(() => {
    return customScores
      .map(score => {
        return formatCustomScore({
          session,
          definition: score.definition,
          customCode: score.customCode,
          rawValue: score.rawValue,
          explanation: score.explanation,
          teamCustomCodes
        });
      })
      .filter((score): score is FormattedCustomScore => score !== null);
  }, [session, customScores, teamCustomCodes]);
}